import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Alert,
  Spinner,
  InputGroup,
  InputGroupText
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import logo from "assets/images/light-logo-coldblock.png"
import { sendCodeRequest, verifyCodeRequest, registerUser, clearMessage, clearError } from "../../store/actions"
import PortalSupportNotice from "components/Common/PortalSupportNotice"

const StepIndicator = ({ step }) => (
  <div className="d-flex align-items-center justify-content-center gap-3 mb-2 mt-4">
    {[1, 2, 3].map(n => (
      <div key={n} className={`d-flex align-items-center`}>
        <div
          className={`rounded-circle d-inline-flex align-items-center justify-content-center`}
          style={{
            width: 34, height: 34,
            background: step >= n ? "#06427a" : "#e9ecef",
            color: step >= n ? "#fff" : "#6c757d",
            fontWeight: 600
          }}
        >
          {n}
        </div>
        {n !== 3 && <div style={{ width: 40, height: 2, background: step > n ? "#06427a" : "#e9ecef", margin: "0 8px" }} />}
      </div>
    ))}
  </div>
)

const Signup = (props) => {
  document.title = "Sign Up - ColdBlock Technologies Inc.";
  const dispatch = useDispatch();
  const {
    stage,
    loading,
    message,
    registrationError,
    email,
    loggedUser
  } = useSelector((state) => state.account || {});
  const [counter, setCounter] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const handleEmailCheckboxChange = (event) => {
    formEmail.setFieldValue(event.target.name, event.target.checked);
  };

  const formEmail = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: "",
      lastName: "",
      email: email || "",
      consentDataProcessing: false,
      consentMarketing: false,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string()
        .trim()
        .test(
          "lastname-length",
          "Last name must be between 2 and 50 characters",
          (value) => {
            if (!value) return true;
            return value.length >= 2 && value.length <= 50;
          }
        ),
      email: Yup.string().email("Invalid email").required("Email is required"),
      consentDataProcessing: Yup.boolean()
        .oneOf([true], "You must consent to the processing of your data to continue"),
    }),
    onSubmit: (values) => {
      const firstName = values.firstName.trim();
      const lastName = (values.lastName || "").trim();
      const email = values.email.trim();
      dispatch(sendCodeRequest(firstName, lastName, email));
      setCounter(30);
    },
  });

  const formCode = useFormik({
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string()
        .length(6, "Code must be 6 digits")
        .required("Enter the code"),
    }),
    onSubmit: (values) => {
      const email = formEmail.values.email.trim();
      const code = values.code.trim();
      dispatch(verifyCodeRequest(email, code, formEmail.values.consentMarketing));
    },
  });

  const formDetails = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Password is required")
        .min(8, "At least 8 characters")
        .matches(/\d/, "Must include at least 1 number")
        .matches(/[A-Z]/, "Must include at least 1 uppercase letter")
        .matches(/[a-z]/, "Must include at least 1 lowercase letter")
        .matches(/[!@#$%^&*()[\]{}:;<>,.?/~_+\-=|\\]/, "Must include at least 1 special character")
        .matches(/^\S+$/, "No spaces allowed"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm your password"),
    }),
    onSubmit: (values) => {
      const email = formEmail.values.email.trim();
      const password = values.password;
      const role = 3;
      dispatch(registerUser(email, password, role, props.router.navigate));
    },
  });

  useEffect(() => {
    let timer;
    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [counter]);

  useEffect(() => {
    if (message || registrationError) {
      const timer = setTimeout(() => {
        dispatch(clearMessage())
      }, 7000)
      return () => clearTimeout(timer)
    }
  }, [message, registrationError, dispatch])

  useEffect(() => {
    if (loggedUser) {
      props.router.navigate("/dashboard");
      dispatch(clearMessage()),
        dispatch(clearError())
    }
  }, [stage, loggedUser, email, props.router]);

  const Brand = (
    <div className="text-center">
      <Link to="/" className="logo-light-element d-inline-block">
        <img src={logo} alt="" height="65" />
      </Link>
    </div>
  );

  const Header = (
    <div className="bg-primary-subtle">
      <Row className="d-flex align-items-center justify-content-between">
        <Col className="col-6">
          <div className="text-primary p-4">
            <h5 className="text-primary white-txt">Create your account</h5>
            {/* <p className="white-txt">3 quick steps to get started.</p> */}
            {/* <p className="white-txt">It’s fast and easy.</p> */}
          </div>
        </Col>
        <Col className="col-6">
          {Brand}
        </Col>
      </Row>
    </div>
  );

  const Banner = (
    <>
      {message && <Alert color="success" className="mb-3">{message} <strong>{email}</strong>.</Alert>}
      {registrationError && <Alert color="danger" className="mb-3">
        {typeof registrationError === "string" ? registrationError : registrationError?.message || "Something went wrong"}
      </Alert>}
    </>
  );

  const password = formDetails.values.password || "";
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()[\]{}:;<>,.?/~_+\-=|\\]/.test(password);
  const hasNoSpaces = password.length > 0 && !/\s/.test(password);

  return (
    <React.Fragment>
      <div className="account-pages d-flex w-100 min-vh-100 py-4 py-md-5 justify-content-center align-items-center">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                {Header}
                <CardBody className="pt-3">
                  <StepIndicator step={Number(stage) || 1} />
                  <div className="p-2">
                    {stage === 1 && (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => { e.preventDefault(); formEmail.handleSubmit(); }}
                      >
                        {Banner}
                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label className="form-label">First name *</Label>
                              <Input
                                name="firstName"
                                type="text"
                                placeholder="John"
                                value={formEmail.values.firstName}
                                onChange={formEmail.handleChange}
                                onBlur={formEmail.handleBlur}
                                invalid={!!(formEmail.touched.firstName && formEmail.errors.firstName)}
                              />
                              {formEmail.touched.firstName && formEmail.errors.firstName && (
                                <FormFeedback type="invalid">{formEmail.errors.firstName}</FormFeedback>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label className="form-label">Last name</Label>
                              <Input
                                name="lastName"
                                type="text"
                                placeholder="Doe"
                                value={formEmail.values.lastName}
                                onChange={formEmail.handleChange}
                                onBlur={formEmail.handleBlur}
                                invalid={!!(formEmail.touched.lastName && formEmail.errors.lastName)}
                              />
                              {formEmail.touched.lastName && formEmail.errors.lastName && (
                                <FormFeedback type="invalid">{formEmail.errors.lastName}</FormFeedback>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <div className="mb-3">
                          <Label className="form-label">Email *</Label>
                          <Input
                            name="email"
                            type="email"
                            placeholder="you@example.com"
                            value={formEmail.values.email}
                            onChange={formEmail.handleChange}
                            onBlur={formEmail.handleBlur}
                            invalid={!!(formEmail.touched.email && formEmail.errors.email)}
                          />
                          {formEmail.touched.email && formEmail.errors.email && (
                            <FormFeedback type="invalid">{formEmail.errors.email}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="form-check mb-3">
                            <Input
                              id="consentDataProcessing"
                              name="consentDataProcessing"
                              type="checkbox"
                              className="form-check-input"
                              checked={formEmail.values.consentDataProcessing}
                              onChange={handleEmailCheckboxChange}
                              onBlur={formEmail.handleBlur}
                              invalid={
                                !!(formEmail.touched.consentDataProcessing &&
                                  formEmail.errors.consentDataProcessing)
                              }
                            />
                            <Label className="form-check-label" for="consentDataProcessing">
                              I consent to the processing of my data for the purpose of handling this
                              specific request, in accordance with the{" "}
                              <a
                                href="https://coldblock.ca/privacy-policy/"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Privacy Policy
                              </a>
                              . *
                            </Label>
                          </div>
                          {formEmail.touched.consentDataProcessing &&
                            formEmail.errors.consentDataProcessing && (
                              <div className="text-danger small mb-2">
                                {formEmail.errors.consentDataProcessing}
                              </div>
                            )}

                          <p className="small fw-bold mt-3 mb-3">
                            From time to time, we would like to contact you about our products and
                            services. If you consent to us contacting you via email for this purpose,
                            please tick below:
                          </p>
                          <div className="form-check mb-3">
                            <Input
                              id="consentMarketing"
                              name="consentMarketing"
                              type="checkbox"
                              className="form-check-input"
                              checked={formEmail.values.consentMarketing}
                              onChange={handleEmailCheckboxChange}
                              onBlur={formEmail.handleBlur}
                            />
                            <Label className="form-check-label" for="consentMarketing">
                              I agree to receive marketing communications from ColdBlock Technologies
                              Inc.
                            </Label>
                          </div>
                          <p className="mb-2 fw-bold small">
                            ColdBlock is committed to protecting and respecting your privacy, and will
                            use your personal information to administer your account and to provide the
                            information you consent to receive from us. You may unsubscribe from our
                            marketing communications at any time by clicking on the &quot;unsubscribe&quot;
                            link in our communications.
                          </p>
                        </div>

                        <p className="small text-muted mb-0">*Required field</p>

                        <div className="mt-3 d-grid">
                          <button className="btn btn-primary primary-btn-bg" type="submit" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : "Sign Up & Verify"}
                          </button>
                        </div>
                        <div className="mt-3 text-center">
                          <span className="text-muted me-2">Already have an account?</span>
                          <Link to="/signin" className="btn btn-link p-0 align-baseline">Sign in</Link>
                        </div>
                      </Form>
                    )}

                    {stage === 2 && (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => { e.preventDefault(); formCode.handleSubmit(); }}
                      >
                        {Banner}
                        <div className="mb-3">
                          <Label className="form-label">Verification code</Label>
                          <Input
                            name="code"
                            type="text"
                            placeholder="Enter code"
                            value={formCode.values.code}
                            maxLength={6}
                            onChange={formCode.handleChange}
                            onBlur={formCode.handleBlur}
                            invalid={!!(formCode.touched.code && formCode.errors.code)}
                          />
                          {formCode.touched.code && formCode.errors.code && (
                            <FormFeedback type="invalid">{formCode.errors.code}</FormFeedback>
                          )}
                        </div>
                        <div className="mt-3 d-grid">
                          <button
                            className="btn btn-primary primary-btn-bg"
                            type="submit"
                            disabled={loading || !formCode.isValid}
                          >
                            {loading ? <Spinner size="sm" /> : "Verify code"}
                          </button>
                        </div>
                        <div className="mt-3 text-center">
                          <div className="mt-3 text-center">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              disabled={loading || counter > 0}
                              onClick={() => {
                                const firstName = formEmail.values.firstName.trim();
                                const lastName = (formEmail.values.lastName || "").trim();
                                const email = formEmail.values.email.trim();
                                dispatch(sendCodeRequest(firstName, lastName, email));
                                setCounter(30);
                              }}
                            >
                              {counter > 0 ? `Resend in ${counter}s` : "Resend code"}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}

                    {stage === 3 && (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => { e.preventDefault(); formDetails.handleSubmit(); }}
                      >
                        {Banner}
                        <Row>
                          {formEmail.values.firstName && (
                            <Col md={6}>
                              <div className="mb-3">
                                <Label className="form-label">First name</Label>
                                <Input
                                  name="firstName"
                                  type="text"
                                  placeholder="John"
                                  disabled
                                  value={formEmail.values.firstName}
                                  onChange={formEmail.handleChange}
                                  onBlur={formEmail.handleBlur}
                                  invalid={!!(formEmail.touched.firstName && formEmail.errors.firstName)}
                                />
                                {formEmail.touched.firstName && formEmail.errors.firstName && (
                                  <FormFeedback type="invalid">{formEmail.errors.firstName}</FormFeedback>
                                )}
                              </div>
                            </Col>
                          )}

                          {formEmail.values.lastName && (
                            <Col md={6}>
                              <div className="mb-3">
                                <Label className="form-label">Last name</Label>
                                <Input
                                  name="lastName"
                                  type="text"
                                  placeholder="Doe"
                                  disabled
                                  value={formEmail.values.lastName}
                                  onChange={formEmail.handleChange}
                                  onBlur={formEmail.handleBlur}
                                  invalid={!!(formEmail.touched.lastName && formEmail.errors.lastName)}
                                />
                                {formEmail.touched.lastName && formEmail.errors.lastName && (
                                  <FormFeedback type="invalid">{formEmail.errors.lastName}</FormFeedback>
                                )}
                              </div>
                            </Col>
                          )}
                        </Row>


                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            name="email"
                            type="email"
                            disabled
                            placeholder="you@example.com"
                            value={formEmail.values.email}
                            onChange={formEmail.handleChange}
                            onBlur={formEmail.handleBlur}
                            invalid={!!(formEmail.touched.email && formEmail.errors.email)}
                          />
                          {formEmail.touched.email && formEmail.errors.email && (
                            <FormFeedback type="invalid">{formEmail.errors.email}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Password</Label>
                          <InputGroup>
                            <Input
                              name="password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Create a password"
                              value={formDetails.values.password}
                              onChange={formDetails.handleChange}
                              onFocus={() => setPasswordFocused(true)}
                              onBlur={(e) => {
                                formDetails.handleBlur(e);
                                setPasswordFocused(false);
                              }}
                              autoComplete="new-password"
                              invalid={!!(formDetails.touched.password && formDetails.errors.password)}
                            />
                            <InputGroupText
                              role="button"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              onClick={() => setShowPassword(v => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              className="bg-transparent"
                              style={{ cursor: "pointer" }}
                            >
                              <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                            </InputGroupText>
                          </InputGroup>
                          {formDetails.touched.password && formDetails.errors.password && (
                            <FormFeedback type="invalid" style={{ display: "block" }}>
                              {formDetails.errors.password}
                            </FormFeedback>
                          )}

                          {passwordFocused && (
                            <div className="p-3 mt-2 rounded bg-light border" style={{ fontSize: "12px" }}>
                              <div className="fw-bold mb-2 text-muted">Password requirements:</div>
                              <Row>
                                <Col md={6}>
                                  <div className={hasMinLength ? "text-success" : "text-danger"}>
                                    <i className={hasMinLength ? "mdi mdi-check-circle-outline me-1" : "mdi mdi-close-circle-outline me-1"} />
                                    At least 8 characters
                                  </div>
                                  <div className={hasUppercase ? "text-success" : "text-danger"}>
                                    <i className={hasUppercase ? "mdi mdi-check-circle-outline me-1" : "mdi mdi-close-circle-outline me-1"} />
                                    At least 1 uppercase letter
                                  </div>
                                  <div className={hasLowercase ? "text-success" : "text-danger"}>
                                    <i className={hasLowercase ? "mdi mdi-check-circle-outline me-1" : "mdi mdi-close-circle-outline me-1"} />
                                    At least 1 lowercase letter
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className={hasNumber ? "text-success" : "text-danger"}>
                                    <i className={hasNumber ? "mdi mdi-check-circle-outline me-1" : "mdi mdi-close-circle-outline me-1"} />
                                    At least 1 number
                                  </div>
                                  <div className={hasSpecial ? "text-success" : "text-danger"}>
                                    <i className={hasSpecial ? "mdi mdi-check-circle-outline me-1" : "mdi mdi-close-circle-outline me-1"} />
                                    At least 1 special character
                                  </div>
                                  <div className={hasNoSpaces ? "text-success" : "text-danger"}>
                                    <i className={hasNoSpaces ? "mdi mdi-check-circle-outline me-1" : "mdi mdi-close-circle-outline me-1"} />
                                    No spaces allowed
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Confirm password</Label>
                          <InputGroup>
                            <Input
                              name="confirmPassword"
                              type={showConfirm ? "text" : "password"}
                              placeholder="Re-enter password"
                              value={formDetails.values.confirmPassword}
                              onChange={formDetails.handleChange}
                              onBlur={formDetails.handleBlur}
                              autoComplete="new-password"
                              invalid={!!(formDetails.touched.confirmPassword && formDetails.errors.confirmPassword)}
                            />
                            <InputGroupText
                              role="button"
                              aria-label={showConfirm ? "Hide password" : "Show password"}
                              onClick={() => setShowConfirm(v => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              className="bg-transparent"
                              style={{ cursor: "pointer" }}
                            >
                              <i className={showConfirm ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                            </InputGroupText>
                          </InputGroup>
                          {formDetails.touched.confirmPassword && formDetails.errors.confirmPassword && (
                            <FormFeedback type="invalid" style={{ display: "block" }}>
                              {formDetails.errors.confirmPassword}
                            </FormFeedback>
                          )}
                        </div>

                        <div className="mt-3 d-grid">
                          <button
                            className="btn btn-primary primary-btn-bg"
                            type="submit"
                            disabled={loading || !formDetails.isValid}
                          >
                            {loading ? <Spinner size="sm" /> : "Create Account"}
                          </button>
                        </div>
                      </Form>
                    )}
                    <div className="mt-3 small text-center text-muted">
                      ColdBlock Technologies Inc.<br />
                      4551 Zimmerman Ave<br />
                      Niagara Falls, ON L2E 3M5<br /> 
                      <a href="mailto:info@coldblock.ca">info@coldblock.ca</a><br />
                    </div>  
                    <div className="mt-3 text-center">
                      <PortalSupportNotice />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Signup);

Signup.propTypes = {
  history: PropTypes.object,
}